import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { candidatesAPI, interviewsAPI, offersAPI } from "../api/client";
import Skeleton from "../components/Skeleton";
import "./Dashboard.css";

const STATUS_LABELS = {
  applied: "Applied",
  screen_pending: "Screen",
  screen_selected: "Screen",
  screen_rejected: "Screen",
  another_profile_requested: "Screen",
  hold: "Screen",
  l1_scheduled: "Interview",
  l2_scheduled: "Interview",
  l1_select: "Shortlisted",
  l2_select: "Shortlisted",
  col_issued: "Offered",
  fol_issued: "Offered",
  joined: "Joined",
};

const STATUS_COLORS = {
  Applied: "#2f7df6",      // Blue
  Screening: "#22b573",    // Green (legacy)
  Screen: "#22b573",       // Green
  Interview: "#7a5af8",    // Purple
  Shortlisted: "#f59f00",  // Orange
  Offered: "#ff6b6b",      // Red-ish
  Joined: "#00bcd4",       // Cyan
  Others: "#95a4bd",       // Grey
};
const SOURCE_COLORS = ["#2f7df6", "#22b573", "#7a5af8", "#ff8a00", "#95a4bd"];
const WALL_CLOCK_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i;

const parseDate = (str) => {
  if (!str) return null;
  const s = String(str);
  return new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
};

export default function Dashboard() {
  const navigate = useNavigate();
  const user = resolveCurrentUser();
  const [topHrLimit, setTopHrLimit] = useState(() => resolveTopHrLimit());
  const [donutPeriod, setDonutPeriod] = useState(() => resolveDonutPeriod());

  const [data, setData] = useState({
    candidates: [],
    interviews: [],
    offers: [],
    errors: {},
    loading: true,
  });

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      const results = await Promise.allSettled([
        candidatesAPI.getAll(),
        interviewsAPI.getAll({ scope: "global" }),
        offersAPI.getAll({ scope: "global" }),
      ]);

      if (!mounted) return;

      const next = {
        candidates: [],
        interviews: [],
        offers: [],
        errors: {},
        loading: false,
      };

      const keys = ["candidates", "interviews", "offers"];
      results.forEach((result, idx) => {
        const key = keys[idx];
        if (result.status === "fulfilled") {
          next[key] = Array.isArray(result.value?.data) ? result.value.data : [];
        } else {
          next.errors[key] = "Unable to load";
        }
      });

      setData(next);
    };

    fetchAll();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("DASHBOARD_TOP_HR_LIMIT", String(topHrLimit));
  }, [topHrLimit]);

  useEffect(() => {
    localStorage.setItem("DASHBOARD_DONUT_PERIOD", donutPeriod);
  }, [donutPeriod]);

  const dashboard = useMemo(
    () => buildDashboard(data.candidates, data.interviews, data.offers, topHrLimit, donutPeriod),
    [data, topHrLimit, donutPeriod]
  );

  return (
    <div className="dashboard-page-v2">
      <section className="dash-headline">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {user.name}</p>
        </div>
      </section>

      <section className="dash-scope-row">
        <span className="scope-pill">Scope: Global</span>
        <span className="scope-pill">KPI Period: This Week</span>
        <span className="scope-pill">Donut Period: {formatDonutPeriod(donutPeriod)}</span>
        <span className="scope-pill">As of: {formatAsOf(dashboard.asOf)}</span>
      </section>

      {data.loading ? (
        <>
          <section className="kpi-grid">
            <div className="kpi-card skeleton-kpi"><Skeleton height={80} borderRadius={12} /></div>
            <div className="kpi-card skeleton-kpi"><Skeleton height={80} borderRadius={12} /></div>
            <div className="kpi-card skeleton-kpi"><Skeleton height={80} borderRadius={12} /></div>
            <div className="kpi-card skeleton-kpi"><Skeleton height={80} borderRadius={12} /></div>
          </section>
          <section className="dash-main-grid">
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, minHeight: 200 }}>
              <Skeleton width="60%" height={18} /><div style={{ height: 16 }} />
              <Skeleton count={3} gap={12} />
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, minHeight: 200 }}>
              <Skeleton width="50%" height={18} /><div style={{ height: 16 }} />
              <Skeleton count={4} gap={10} />
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, minHeight: 200 }}>
              <Skeleton width="55%" height={18} /><div style={{ height: 16 }} />
              <Skeleton count={3} gap={12} />
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, minHeight: 200, gridColumn: 'span 2' }}>
              <Skeleton width="65%" height={18} /><div style={{ height: 16 }} />
              <Skeleton count={4} gap={10} />
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, minHeight: 120 }}>
              <Skeleton width="40%" height={18} /><div style={{ height: 16 }} />
              <Skeleton width="80px" height={36} borderRadius={8} />
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="kpi-grid">
            <KPI title="Total Candidates" value={dashboard.kpis.totalCandidates.value} delta={dashboard.kpis.totalCandidates.delta} color="blue" />
            <KPI title="Total Interviews Scheduled" value={dashboard.kpis.interviewsScheduled.value} delta={dashboard.kpis.interviewsScheduled.delta} color="green" />
            <KPI title="Total Offers Issued" value={dashboard.kpis.offersIssued.value} delta={dashboard.kpis.offersIssued.delta} color="violet" />
            <KPI title="Total Joined" value={dashboard.kpis.joined.value} delta={dashboard.kpis.joined.delta} color="orange" />
          </section>
          <section className="dash-main-grid">
            <Card title="Pipeline Movement (Status Changes)" error={data.errors.candidates} action={(
              <select className="dash-select" value={donutPeriod} onChange={(e) => setDonutPeriod(e.target.value)}>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="overall">Overall</option>  
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
              </select>
            )}>
              <DonutChart total={dashboard.applicationTotal} items={dashboard.applicationOverview} />
            </Card>
            <Card title="Recent Activities (Latest Global Events)" error={Object.keys(data.errors).length ? "Some activity sources failed" : ""}>
              <ActivityList items={dashboard.recentActivities} />
            </Card>
            <Card title="Upcoming Interviews (Global Schedule)" error={data.errors.interviews}>
              <UpcomingInterviews items={dashboard.upcomingInterviews} onViewAll={() => navigate("/interviews")} />
            </Card>
            <Card title="Candidates by HR (Total Candidates Created)" error={data.errors.candidates} className="wide-card" action={(
              <select className="dash-select" value={topHrLimit} onChange={(e) => setTopHrLimit(e.target.value)}>
                <option value="all">All HRs</option>
                {[4, 6, 8, 10, 12, 15, 20].map((limit) => (<option key={limit} value={limit}>Top {limit}</option>))}
              </select>
            )}>
              <SourceBars items={dashboard.sourceOverview} />
            </Card>
            <Card title="Quick Actions" className="quick-card">
              <QuickActions navigate={navigate} />
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

function KPI({ title, value, delta, color }) {
  return (
    <article className={`kpi-card ${color}`}>
      <p className="kpi-title">{title}</p>
      <h2>{value}</h2>
      <p className="kpi-delta">{delta >= 0 ? `+${delta}` : delta} in this week</p>
    </article>
  );
}

function Card({ title, children, error, className = "", action = null }) {
  return (
    <article className={`dash-card ${className}`.trim()}>
      <div className="dash-card-header">
        <div className="dash-card-title"><h3>{title}</h3></div>
        <div className="dash-card-tools">
          {action}
          {error ? <span className="warn-pill">{error}</span> : null}
        </div>
      </div>
      {children}
    </article>
  );
}

function DonutChart({ total, items }) {
  const fallback = [{ label: "No Data", value: 1, percent: 100, color: "#d3dcea" }];
  const chartItems = items.length ? items : fallback;

  let cumulative = 0;
  const segments = chartItems.map((item, idx) => {
    const start = cumulative;
    cumulative += item.percent;
    // Force the final segment to close at 100% to avoid visual overflow gaps.
    const end = idx === chartItems.length - 1 ? 100 : cumulative;
    return { ...item, start, end };
  });

  return (
    <div className="donut-wrap">
      <div className="donut" style={{ background: toConic(segments) }}>
        <div className="donut-hole">
          <strong>{total}</strong>
          <span>In Period</span>
        </div>
      </div>
      <ul className="legend-list">
        {chartItems.map((item, idx) => (
          <li key={`${item.label}-${idx}`}>
            <span className="dot" style={{ background: item.color }} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivityList({ items }) {
  if (!items.length) {
    return <p className="dash-empty">No recent activities yet.</p>;
  }

  return (
    <div className="activity-list">
      {items.map((item) => (
        <div className="activity-item" key={item.id}>
          <div className={`activity-dot ${item.type}`} />
          <div>
            <p className="activity-title">{item.title}</p>
            <p className="activity-time">{item.timeAgo}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function UpcomingInterviews({ items, onViewAll }) {
  return (
    <div>
      <div className="card-action-row">
        <button type="button" className="link-btn" onClick={onViewAll}>View All</button>
      </div>
      {!items.length ? (
        <p className="dash-empty">No upcoming interviews.</p>
      ) : (
        <div className="upcoming-list">
          {items.map((item) => (
            <article className="upcoming-item" key={item.id}>
              <p className="up-role">{item.role}</p>
              <p className="up-name">{item.name}</p>
              <p className="up-meta">{item.date} | {item.time}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceBars({ items }) {
  if (!items.length) {
    return <p className="dash-empty">No HR ownership data found. New candidates will populate this chart.</p>;
  }

  const max = Math.max(...items.map((i) => i.value), 1);

  return (
    <div className="bars-scroll">
      <div className="bars-wrap">
        {items.map((item, idx) => (
          <div className="bar-group" key={`${item.label}-${idx}`}>
            <div className="bar-value">{item.value}</div>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ height: `${(item.value / max) * 100}%`, background: item.color }}
              />
            </div>
            <p className="bar-label">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickActions({ navigate }) {
  const actions = [
    { label: "Add Candidate", action: () => navigate("/candidates") },
    { label: "Schedule Interview", action: () => navigate("/interviews") },
    { label: "AI Assistant", action: () => navigate("/ai") },
  ];

  return (
    <div className="quick-grid">
      {actions.map((item) => (
        <button key={item.label} className="quick-btn" type="button" onClick={item.action}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

function safeDecode(token) {
  try {
    const parsed = jwtDecode(token);
    return {
      name: parsed?.full_name || parsed?.name || extractNameFromEmail(parsed?.email || parsed?.upn) || "User",
      role: parsed?.role || "No Role",
    };
  } catch {
    return { name: "User", role: "No Role" };
  }
}

function resolveCurrentUser() {
  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const parsedUser = JSON.parse(rawUser);
      const storedName =
        parsedUser?.full_name ||
        parsedUser?.name ||
        parsedUser?.username ||
        extractNameFromEmail(parsedUser?.email);
      if (storedName) {
        return { name: storedName, role: parsedUser?.role || "No Role" };
      }
    }
  } catch {
    // ignore malformed localStorage user
  }

  const token = localStorage.getItem("token");
  return token ? safeDecode(token) : { name: "User", role: "No Role" };
}

function extractNameFromEmail(email) {
  if (!email || typeof email !== "string" || !email.includes("@")) return "";
  return email.split("@")[0];
}

function buildDashboard(candidates, interviews, offers, topHrLimit, donutPeriod) {
  const now = new Date();
  const day = now.getDay();
  const startOfThisWeek = new Date(now);
  const offsetToMonday = day === 0 ? -6 : 1 - day;

  startOfThisWeek.setDate(now.getDate() + offsetToMonday);
  startOfThisWeek.setHours(0, 0, 0, 0);

  const weekStart = startOfThisWeek;
  const weekEnd = now;

  const candidateDates = candidates.map((c) => new Date(c.created_at));
  const candidateDelta = countInRange(candidateDates, weekStart, weekEnd);

  const scheduledInterviewStatuses = new Set(["scheduled", "rescheduled", "pending"]);
  const activeInterviews = interviews.filter((i) => {
    const status = String(i?.status || "").toLowerCase();
    const scheduledDate = parseWallClockDateTime(i?.scheduled_date);
    return (
      scheduledDate &&
      !isClosedStatus(status) &&
      (scheduledInterviewStatuses.has(status) || scheduledDate >= now)
    );
  });
  const interviewsScheduled = activeInterviews.length;
  const interviewDelta = countInRange(
    activeInterviews
      .map((i) => parseWallClockDateTime(i.scheduled_date))
      .filter(Boolean),
    weekStart,
    weekEnd
  );
  const offersIssued = candidates.filter((c) => ["col_issued", "fol_issued"].includes(c.status)).length;
  const offerDelta = countInRange(
    candidates
      .filter((c) => ["col_issued", "fol_issued"].includes(c.status))
      .map((c) => new Date(c.updated_at)),
    weekStart,
    weekEnd
  );
  const joinedCount = candidates.filter((c) => c.status === "joined").length;
  const joinedDelta = countInRange(
    candidates
      .filter((c) => c.status === "joined")
      .map((c) => new Date(c.updated_at)),
    weekStart,
    weekEnd
  );

  const { start: donutStart, end: donutEnd } = getDonutRange(donutPeriod, now);
  const donutCandidates = candidates.filter((c) => {
    const point = new Date(c.updated_at ?? c.created_at);
    return point >= donutStart && point <= donutEnd;
  });

  const statusCountsMap = donutCandidates.reduce((acc, candidate) => {
    const label = STATUS_LABELS[candidate.status] || "Others";
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  const orderedLabels = [
    "Applied",
    "Screening",
    "Interview",
    "Shortlisted",
    "Offered",
    "Joined",
    "Others"
  ];

  const overviewTotal = orderedLabels.reduce(
    (sum, label) => sum + (statusCountsMap[label] || 0),
    0
  );

  const applicationOverview = orderedLabels
    .map((label, idx) => ({
      label,
      value: statusCountsMap[label] || 0,
      percent: overviewTotal ? ((statusCountsMap[label] || 0) / overviewTotal) * 100 : 0,
      color: STATUS_COLORS[label] || "#95a4bd",
    }))
    .filter((item) => item.value > 0);

  const sourceMap = candidates.reduce((acc, candidate) => {
    const hrName = normalizeHrName(candidate.created_by_name);
    acc[hrName] = (acc[hrName] || 0) + 1;
    return acc;
  }, {});

  const rankedSources = Object.entries(sourceMap).sort((a, b) => b[1] - a[1]);
  const showAllHrs = String(topHrLimit).toLowerCase() === "all";
  const hrLimit = showAllHrs ? rankedSources.length : Math.max(Number(topHrLimit) || 4, 1);
  const primarySources = rankedSources.slice(0, hrLimit);
  const otherSourcesCount = showAllHrs
    ? 0
    : rankedSources.slice(hrLimit).reduce((sum, [, value]) => sum + value, 0);

  const sourceOverview = [
    ...primarySources.map(([label, value], idx) => ({
      label,
      value,
      color: SOURCE_COLORS[idx % SOURCE_COLORS.length],
    })),
    ...(otherSourcesCount > 0
      ? [{ label: "Others", value: otherSourcesCount, color: SOURCE_COLORS[4] }]
      : []),
  ];

  const upcomingInterviews = interviews
    .map((i) => ({
      id: i.id,
      role: i.position || i.interview_type || "Interview",
      name: i.candidate_name || "Candidate",
      status: String(i.status || "").toLowerCase(),
      dateTime: parseWallClockDateTime(i.scheduled_date),
    }))
    .filter((i) => i.dateTime && i.dateTime >= now && !isClosedStatus(i.status))
    .sort((a, b) => a.dateTime - b.dateTime)
    .slice(0, 4)
    .map((i) => ({
      id: i.id,
      role: i.role,
      name: i.name,
      date: i.dateTime.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
      time: i.dateTime.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
    }));

  const activities = [
    ...candidates.slice(0, 8).map((c) => ({
      id: `c-${c.id}`,
      type: "candidate",
      title: `${fullName(c)} added for ${c.position || "Open Role"}`,
      date: new Date(c.created_at || c.updated_at || Date.now()),
    })),
    ...interviews.slice(0, 8).map((i) => ({
      id: `i-${i.id}`,
      type: "interview",
      title: `Interview scheduled with ${i.candidate_name || "candidate"}`,
      date: new Date(i.created_at || i.scheduled_date || Date.now()),
    })),
    ...offers.slice(0, 8).map((o) => ({
      id: `o-${o.id || o.offer_id || Math.random()}`,
      type: "offer",
      title: `Offer updated for ${o.candidate_name || "candidate"}`,
      date: new Date(o.offer_updated_at || o.offer_created_at || o.updated_at || o.created_at),
    })),
  ]
    .filter((a) => !Number.isNaN(a.date.getTime()))
    .sort((a, b) => b.date - a.date)
    .slice(0, 6)
    .map((a) => ({ ...a, timeAgo: timeAgo(a.date) }));

  return {
    kpis: {
      totalCandidates: { value: candidates.length, delta: candidateDelta },
      interviewsScheduled: { value: interviewsScheduled, delta: interviewDelta },
      offersIssued: { value: offersIssued, delta: offerDelta },
      joined: { value: joinedCount, delta: joinedDelta },
    },
    applicationOverview,
    applicationTotal: overviewTotal,
    sourceOverview,
    recentActivities: activities,
    upcomingInterviews,
    asOf: now,
  };
}

function toConic(segments) {
  const parts = segments.map((s) => `${s.color} ${s.start}% ${s.end}%`);
  return `conic-gradient(${parts.join(",")})`;
}

function countInRange(dates, start, end) {
  return dates.filter((d) => d >= start && d <= end).length;
}

function fullName(candidate) {
  return `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim() || "Candidate";
}

function normalizeHrName(value) {
  const safeName = String(value || "").trim();
  return safeName || "Unassigned";
}

function resolveTopHrLimit() {
  const fromStorage = localStorage.getItem("DASHBOARD_TOP_HR_LIMIT");
  const rawValue = fromStorage || process.env.REACT_APP_DASHBOARD_TOP_HR_LIMIT || "4";
  if (String(rawValue).toLowerCase() === "all") return "all";
  const parsed = Number.parseInt(rawValue, 10);
  if (!Number.isFinite(parsed)) return "4";
  return String(Math.min(Math.max(parsed, 1), 20));
}

function resolveDonutPeriod() {
  const saved = localStorage.getItem("DASHBOARD_DONUT_PERIOD");
  const allowed = ["last_week", "last_month", "last_3_months", "overall", "this_week", "this_month"];
  if (saved && allowed.includes(saved)) {
    return saved;
  }
  return "this_week";
}

function formatAsOf(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDonutPeriod(period) {
  if (period === "last_month") return "Last Month";
  if (period === "last_3_months") return "Last 3 Months";
  if (period === "overall") return "Overall";
  if (period === "this_week") return "This Week";
  if (period === "this_month") return "This Month";
  return "Last Week";
}

function getLastWeekRange(referenceDate) {
  const now = new Date(referenceDate);
  const day = now.getDay(); // 0=Sun,1=Mon...
  const startOfThisWeek = new Date(now);
  const offsetToMonday = day === 0 ? -6 : 1 - day;
  startOfThisWeek.setDate(now.getDate() + offsetToMonday);
  startOfThisWeek.setHours(0, 0, 0, 0);

  const start = new Date(startOfThisWeek);
  start.setDate(start.getDate() - 7);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

function getDonutRange(period, referenceDate) {
  const now = new Date(referenceDate);

  if (period === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return { start, end };
  }

  if (period === "last_3_months") {
    const start = new Date(now.getFullYear(), now.getMonth() - 3, 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return { start, end };
  }

  if (period === "overall") {
    return { start: new Date(0), end: now };
  }

  if (period === "this_week") {
    const day = now.getDay(); // 0=Sun,1=Mon...
    const startOfThisWeek = new Date(now);
    const offsetToMonday = day === 0 ? -6 : 1 - day;
    startOfThisWeek.setDate(now.getDate() + offsetToMonday);
    startOfThisWeek.setHours(0, 0, 0, 0);
    return { start: startOfThisWeek, end: now };
  }
  
  if (period === "this_month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }

  return getLastWeekRange(now);
}

function timeAgo(date) {
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${minutes || 1} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

function isClosedStatus(statusValue) {
  const status = String(statusValue || "").toLowerCase();
  return status.includes("cancel") || status.includes("complete") || status.includes("reject");
}

function parseWallClockDateTime(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const input = String(value || "").trim();
  if (!input) return null;

  // Keep interview datetime exactly as scheduled wall-clock time.
  const match = input.match(WALL_CLOCK_DATE_TIME_PATTERN);
  if (match) {
    const [, year, month, day, hour, minute, second = "00"] = match;
    const localDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
    return Number.isNaN(localDate.getTime()) ? null : localDate;
  }

  const fallback = new Date(input);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}
